import "../../index.scss";
import "./rootPage.scss";
import {
  useLocation,
  Location,
  useParams,
  useNavigate,
} from "react-router-dom";
import { getEndpoints } from "../RouteTypes";
import { isDefined, translations } from "@timeedit/registration-shared";
import {
  Navbar,
  useIsDesktop,
  useNotices,
} from "@timeedit/registration-components";
import { useLanguage } from "../../lang/translations";
import {
  isValidBasePath,
  removeFirstLastBackslash,
  removePathParameters,
} from "../URLUtil";
import { Layout } from "@timeedit/ui-components/antd";
import { LocaleWrapper } from "../../lang/locale";
import { ResponsiveTopBar } from "./DesktopNavigation";
import { useAppSelector } from "../../redux/hooks";
import { SkipToContent, ThemeWrapper } from "@timeedit/ui-components";
import { PageOutlet } from "./PageOutlet";
import UnorderedListOutlined from "@timeedit/ui-components/icons/UnorderedListOutlined";
import CalendarOutlined from "@timeedit/ui-components/icons/CalendarOutlined";

export function Root(): React.JSX.Element {
  const ENDPOINTS = getEndpoints();
  const navigationEndpoints = [
    {
      ...ENDPOINTS.registration,
      icon: <UnorderedListOutlined alt={ENDPOINTS.registration.name} />,
    },
    {
      ...ENDPOINTS.schedule,
      icon: <CalendarOutlined alt={ENDPOINTS.schedule.name} />,
    },
  ];
  const parameters = useParams<{
    customerSignature: string;
  }>();

  const { currentLanguage } = useLanguage();
  const { loginData } = useAppSelector((state) => state.login);

  const location = useLocation();
  const navigate = useNavigate();

  const isDesktop = useIsDesktop();
  const index = !isDesktop ? getIndexFromLocation(location) : 0;

  const notice = useAppSelector((state) => state.notice);
  const noticeContext = useNotices(notice);
  return (
    <ThemeWrapper scope="scope-registration" defaultComponentSize="middle">
      <LocaleWrapper localeId={currentLanguage}>
        {noticeContext}
        <Layout className="root-page__background">
          <SkipToContent href={"#main"} />
          <ResponsiveTopBar isDesktop={isDesktop} />

          <PageOutlet />
          {!isDesktop && (
            <Navbar
              index={index}
              onChange={navigateToIndex}
              Endpoints={navigationEndpoints}
              tabsProps={{ className: "root-page__navbar-tabs" }}
              navigateTranslation={translations.navigate}
            />
          )}
        </Layout>
      </LocaleWrapper>
    </ThemeWrapper>
  );

  function isRoot(path: string): boolean {
    return path === ENDPOINTS.root.path;
  }

  function getEndpointFromIndex(index: number): "registration" | "schedule" {
    const endpoint = navigationEndpoints.find((_, value) => value === index);

    if (isDefined(endpoint)) {
      return endpoint.path;
    }

    throw new Error(`There was no endpoint found with index ${index}`);
  }

  function getBasePathFromLocation(
    location: Location
  ): "" | "registration" | "schedule" | "registration/:id" {
    const pathName = location.pathname;

    const pathWithoutParameters = removePathParameters({
      path: pathName,
      params: parameters,
    });

    const basePath = removeFirstLastBackslash(pathWithoutParameters);

    if (
      isValidBasePath(basePath, ENDPOINTS, loginData?.customerSignature || "")
    ) {
      return basePath;
    }

    throw new Error(
      `Invalid base path ${basePath}, make sure it exists in endpoints`
    );
  }

  function getIndexFromLocation(location: Location): number {
    const basePath = getBasePathFromLocation(location);

    if (isRoot(basePath)) {
      return navigationEndpoints.findIndex(
        ({ path }) => path === ENDPOINTS.registration.path
      );
    }

    // Can give -1 if it doesn't find
    return navigationEndpoints.findIndex(({ path }) => basePath.includes(path));
  }

  function navigateToIndex(index: number): void {
    const endpoint = getEndpointFromIndex(index);
    navigate(`${endpoint}`);
  }
}
